<template>
  <div class="columns is-marginless">
    <div class="column is-four-fifths is-offset-1">
      <div class="edit-member">

        <b-loading :active="loading" />

        <router-link to="/member/list" class="edit-member__prev-page">
          <i class="icon-chevron-left-bold" />
          返回人員管理
        </router-link>

        <member-form
          v-model="member"
          :has-empty-department="false"
          mode="edit"
          class="mb-8 mt-5"
          is-active
          @input="v$.$touch">
          <template #title>
            <h2 class="edit-member__subtitle">
              人員資訊
              <span
                :class="['edit-member__subtitle--pill', memberStatusPill.class]">
                {{ memberStatusPill.text }}
              </span>
            </h2>
          </template>
          <template #action>
            <div v-if="v$.$dirty" class="edit-member__action">
              <w-button
                :disabled="v$.$invalid || loading"
                :loading="loading"
                @click="submit"
              >
                儲存
              </w-button>
            </div>
          </template>
        </member-form>

        <b-collapse
          class="card mb-8"
          animation="slide">
          <template #trigger="props">
            <div
              class="card-header"
              role="button">
              <label class="font-medium text-black">帳戶安全</label>
              <a>
                <b-icon :icon="props.open ? 'caret-up' : 'caret-down'" />
              </a>
            </div>
          </template>
          <div class="card-content py-2">
            <div class="container">
              <div class="flex items-center">
                <label class="text-gray-500 mr-8">變更密碼</label>
                <w-button
                  type="outlined"
                  @click="onShowResetPwd"
                >
                  重置密碼
                </w-button>
              </div>
            </div>
          </div>
          <w-modal
            v-model="showResetPwdDialog"
            :can-cancel="false"
            :width="448"
            @close="onCloseResetPwdDialog()"
          >
            <template #header>
              <h1>
                重置密碼
              </h1>
            </template>
            <template #content>
              <p>重置後請使用預設密碼登入系統。</p>
              <p>預設密碼：a{{identification}}</p>
            </template>
            <template #footer>
              <w-button
                :disabled="isResettingPassword"
                :loading="isResettingPassword"
                type="outlined"
                @click="onResetPwd()"
              >
                確定
              </w-button>
            </template>
          </w-modal>
        </b-collapse>

        <b-collapse
          v-if="member.verified && !isMemberDisabled"
          class="card mb-8"
          animation="slide">
          <template #trigger="props">
            <div
              class="card-header"
              role="button">
              <label class="text-black font-medium">刪除帳號</label>
              <a>
                <b-icon :icon="props.open ? 'caret-up' : 'caret-down'" />
              </a>
            </div>
          </template>
          <div class="card-content py-2">
            <div class="edit-member__disable">
              <div class="level">
                <div class="level-left">
                  <w-button
                    :disabled="!isNormalMember"
                    color-type="error"
                    type="outlined"
                    @click="onDeleteMember(member.id)">
                    刪除帳號
                  </w-button>
                  <div class="ml-2">
                    <p v-if="isNormalMember">
                      <span v-if="!isMemberDisabled">
                        刪除帳號後，該帳號將無法享受好彙訂便捷的訂購服務。
                      </span>
                    </p>
                    <p v-else>如需刪除帳號，請先將角色轉換成一般人員。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
      </div>

      <w-modal v-model="showConfirmModal" type="danger">
        <template #header>
          <h1>確認刪除人員</h1>
        </template>
        <template #content>
          <div class="flex">
            <i class="icon-alert-circle mr-4">
              <span class="path1" />
              <span class="path2" />
              <span class="path3" />
            </i>
            <p>請注意，帳號經刪除後將無法繼續登入使用，尚未領取的訂單亦將自動取消。請確認是否刪除？</p>
          </div>
        </template>
        <template #footer>
          <w-button
            :disabled="isDeletingMember"
            :loading="isDeletingMember"
            color-type="error"
            @click="onDeleteMember(member.id)">
            刪除帳號
          </w-button>
        </template>
      </w-modal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import _cloneDeep from 'lodash/cloneDeep';
import useVuelidate from '@vuelidate/core';

import { batchDeleteMembers, resetMemberPassword } from '@/api/member';
import MemberForm from '@/components/Member/MemberForm.vue';
import {
  MEMBER_STATUS,
  NO_DEPARTMENT_ID,
  SNACKBAR_TYPES,
  USER_LEVEL,
} from '@/utils/constants';
import {
  DISABLE_MEMBER,
  ENABLE_MEMBER,
  GET_MEMBER_INFO,
  UPDATE_MEMBER,
} from '@/store/member/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import { USER_LOGOUT } from '@/store/user/constants';

export default {
  name: 'MemberEdit',
  components: {
    MemberForm,
  },
  data() {
    return {
      isDeletingMember: false,
      isResettingPassword: false,
      loading: false,
      mode: 'info',
      member: {
        member_detail: {},
        latest_verification_or_invitation_email: {},
      },
      memberRole: null,
      showConfirmModal: false,
      showResetPwdDialog: false,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  async mounted() {
    const { memberId } = this.$route.params;

    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.loading = true;
    const departments = await this.getDepartmentList();
    this.departments = departments.filter((d) => d.id !== NO_DEPARTMENT_ID);
    await this.getMember(memberId);
    this.loading = false;
    this.v$.$reset();
  },
  computed: {
    ...mapState('user', {
      currentUserRole: ({ role }) => role,
      identification: ({ identification }) => identification,
    }),
    isNormalMember() {
      return this.memberRole === USER_LEVEL.NORMAL;
    },
    isMemberDisabled() {
      return this.member.status === MEMBER_STATUS.DISABLED;
    },
    memberStatusPill() {
      if (!this.member.verified) {
        return {
          text: '待驗證',
          class: 'danger',
        };
      }
      if (this.member.status === MEMBER_STATUS.ENABLED) {
        return {
          text: '已啟用',
          class: null,
        };
      }
      return {
        text: '',
        class: null,
      };
    },
  },
  methods: {
    async getMember(id) {
      try {
        const member = await this.getMemberInfo(id);
        this.member = {
          ...member,
          department_id: member.department_id ?? NO_DEPARTMENT_ID,
        };
        this.memberRole = this.member.role;
      } catch (ex) {
        const errorMessage = ex?.message ? `(${ex.message})` : '';
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: `取得人員資訊時發生錯誤，請重試一次${errorMessage}` });
      }
    },
    async submit() {
      try {
        const { memberId } = this.$route.params;
        const member = _cloneDeep(this.member);

        this.loading = true;

        /* Remove unnecessary value */
        const valuesToKeep = ['allow_order_on_site', 'department_id', 'email', 'member_detail', 'name', 'role', 'status'];
        Object.keys(member).forEach((key) => {
          if (!valuesToKeep.includes(key)) {
            delete member[key];
          }
        });

        /* Prevent update role if current user if owner */
        if (member.role === USER_LEVEL.OWNER || member.role > this.currentUserRole) {
          delete member.role;
        }

        // NOTE: API requires null for no department
        if (member.department_id === NO_DEPARTMENT_ID) {
          member.department_id = null;
        }

        await this.updateMember({ id: memberId, data: member });
        await this.getMember(memberId);
        this.v$.$reset();
        this.$snackbar({ type: SNACKBAR_TYPES.SUCCESS, title: '人員資訊已更新' });
      } catch (ex) {
        const errorTitles = {
          9013: '人員編號重複',
        };
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: errorTitles?.[ex.status] || '更新人員時發生錯誤，請重試一次', msg: ex.message || '' });
      } finally {
        this.loading = false;
      }
    },
    onCloseResetPwdDialog() {
      this.showResetPwdDialog = false;
    },
    onShowResetPwd() {
      if (!this.identification) {
        this.logout();
        this.$router.replace('/login');
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: '無法取得公司統一編號，請重新登入。' });
        return;
      }
      this.showResetPwdDialog = true;
    },
    async onDeleteMember(memberId) {
      try {
        if (!this.showConfirmModal) {
          this.showConfirmModal = true;
          return;
        }
        this.isDeletingMember = true;
        await batchDeleteMembers([memberId]);
        this.$router.replace('/member/list');
        this.$snackbar({ type: SNACKBAR_TYPES.SUCCESS, title: '刪除成功', msg: '人員已成功刪除' });
        this.showConfirmModal = false;
      } catch (ex) {
        await this.getMember(memberId);
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: '刪除失敗', msg: ex.message });
      } finally {
        this.isDeletingMember = false;
      }
    },
    async onResetPwd() {
      this.isResettingPassword = true;
      try {
        await resetMemberPassword(this.member.id);
        this.showResetPwdDialog = false;
        this.$snackbar({ type: SNACKBAR_TYPES.SUCCESS, title: '密碼重置成功' });
      } catch (ex) {
        const errorMessage = ex?.message ? `(${ex.message})` : '';
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: `重設密碼失敗，請重試一次${errorMessage}` });
      } finally {
        this.isResettingPassword = false;
      }
    },
    ...mapActions('member', {
      updateMember: UPDATE_MEMBER,
      enableMember: ENABLE_MEMBER,
      disableMember: DISABLE_MEMBER,
      getMemberInfo: GET_MEMBER_INFO,
    }),
    ...mapActions('user', {
      logout: USER_LOGOUT,
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
  },
};
</script>

<style lang="scss" scoped>
.card {
  box-shadow: 0px 0px 4px 0px $lighter-grey;
  transition: box-shadow 0.2s ease-in-out;
  &:hover {
    box-shadow: 0px 0px 10px 0px $lighter-grey;
  }

  .card-header {
    @apply border-b border-light-gray-500 flex items-center justify-between px-6 py-2 shadow-none;
  }
}

.edit-member {
  @apply bg-white mt-4 pb-6 pt-3 px-4 rounded-sm;

  &__prev-page {
    @apply font-bold text-gray-500 text-sm;

    i {
      vertical-align: middle;
    }
    &:hover {
      color: $blue;
      i::before {
        color: $blue;
      }
    }
  }
  &__action {
    @apply flex items-center justify-end;
  }
  &__title {
    margin-top: 1rem;
    padding: 0;
    &:focus {
      box-shadow: none;
    }
  }
  &__subtitle {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 1rem;
    &--pill {
      font-size: .75rem;
      color: $white;
      background-color: $blue;
      padding: .1rem .5rem;
      margin-left: .5rem;
      border-radius: .75rem;
      vertical-align: text-bottom;
      &.danger {
        background-color: $red;
      }
      &.silent {
        background-color: $dark-grey;
      }
    }
  }
  &__disable {
    color: $dark-grey;
    button.is-danger:not([disabled]):hover {
      background-color: $red;
    }
  }
  &__history {
    border-collapse: separate;
    border-spacing: 0 1rem;
    &--title {
      color: $dark-grey;
    }
    &--value {
      padding-left: 1rem;
    }
  }

  ::v-deep.card {
    box-shadow: 0px 0px 4px 1px $lighter-grey;
    border-radius: 4px;
    &-header {
      box-shadow: none;
      border-bottom: 1px solid map.get($custom-colors, "light-grey-500");
      border-radius: .25rem 0;
    }
  }
}

::v-deep .field-label {
  margin-right: 0;
  text-align: left;
  label {
    color: $dark-grey;
    font-weight: 400;
  }
  .required {
    &::after {
      content: '\204e';
      margin-left: 3px;
      color: $red;
      vertical-align: text-bottom;
    }
  }
}
</style>
